import { useQuery } from "@tanstack/react-query";
import { stringify } from "qs";

import useCurrentUser from "~/hooks/user/useCurrentUser";
import {
  getPagedCollection,
  getPublicPagedCollection,
} from "~/lib/payload/collection";
import type { CollectionJoinParams, CollectionNames } from "~/types";

interface PaginationParams {
  archived?: boolean;
  organizationId?: string;
  collection: CollectionNames;
  limit?: number;
  page?: number | undefined | null;
  where?: any;
  sort?: string | string[] | undefined | null;
  depth?: number;
  join?: Array<CollectionJoinParams>;
  publicView?: boolean; // If the collection is public facing.
}

export interface UsePaginationData {
  totalItems?: number;
  totalPages?: number;
  hasNextPage?: boolean;
  hasPrevPage?: boolean;
  nextPage?: number;
  prevPage?: number;
  page?: number;
  data?: any[];
  isLoading?: boolean;
}

export interface UsePaginationParams extends PaginationParams {
  serialLoading?: boolean;
  refreshInterval?: number;
}

/**
 * @function getPaginationKey
 * @description Function to get the pagination key
 * @param {PaginationParams} params - The parameters for the pagination key
 * @returns {string | null} - The pagination key
 */
const getPaginationKey = ({
  organizationId,
  collection,
  limit,
  page,
  where,
  sort,
  depth,
}: PaginationParams): string | null => {
  if (!collection) return null;

  const params = stringify(
    {
      page,
      limit,
      where,
      sort,
      depth,
    },
    {
      skipNulls: true,
      addQueryPrefix: false,
      allowDots: true,
    }
  );

  return `${organizationId}-${collection}-${params}`;
};

/**
 * @function getPaginationData
 * @description Function to get the pagination data
 * @param {PaginationParams} params - The parameters for the pagination data
 * @returns {Promise<PaginationData>} - The pagination data
 */
const getPaginationData = async ({
  archived,
  collection,
  limit,
  page,
  where,
  sort,
  join,
  depth,
  publicView,
}: PaginationParams): Promise<UsePaginationData> => {
  let data;

  // If the collection is public facing, use the public facing function.
  if (publicView) {
    data = await getPublicPagedCollection({
      archived,
      collection,
      page,
      limit,
      sort,
      where,
      join,
      depth,
    });
  }

  // If user has to be authenticated to view the collection.
  if (!publicView) {
    data = await getPagedCollection({
      archived,
      collection,
      page,
      limit,
      sort,
      where,
      join,
      depth,
    });
  }

  // Return the data.
  return {
    totalItems: data?.totalDocs,
    totalPages: data?.totalPages,
    hasNextPage: data?.hasNextPage,
    hasPrevPage: data?.hasPrevPage,
    nextPage: data?.nextPage,
    prevPage: data?.prevPage,
    page: data?.page,
    data: data?.docs || [],
  };
};

interface usePaginationHandlerParams extends UsePaginationParams {
  organizationId: string;
  userLoading: boolean;
  publicView?: boolean;
}

/**
 * @function usePaginationHandler
 * @description Wrapper for public and private pagination hooks
 * @param {UsePaginationParams} params - The parameters for the pagination
 * @returns {object} - The pagination data and loading state
 */
function usePaginationHandler<T>({
  archived,
  collection,
  limit = 10,
  page = 1,
  where,
  sort,
  join,
  depth = 1,
  serialLoading = false,
  userLoading,
  organizationId,
  publicView,
  refreshInterval,
}: usePaginationHandlerParams): {
  data: T[];
  totalPages: number | undefined;
  totalItems: number | undefined;
  currentPage: number | undefined;
  isLoading: boolean;
} {
  const pageInt = parseInt(page?.toString() || "1", 10);

  // Need to get the total count of items for pagination.
  // This is separate as the count gets lost by Payload as
  // the page number increases.
  const countKey = getPaginationKey({
    organizationId,
    collection,
    limit,
    page: 1,
    where,
    sort,
    depth,
  });

  const runCountQuery = Boolean(!serialLoading && !userLoading && collection);

  const { data: countData, isLoading: countIsLoading } = useQuery({
    // We use the countKey here to cache the first page data, so do not need a separate key.
    // Only thing to consider is publicView is not included in the key, but that should be fine
    // as long as public and private collections are not mixed.
    // eslint-disable-next-line @tanstack/query/exhaustive-deps
    queryKey: [countKey],
    queryFn: () =>
      getPaginationData({
        archived,
        collection,
        limit,
        page: 1,
        where,
        sort,
        join,
        depth,
        publicView,
      }),
    enabled: runCountQuery,
    refetchOnWindowFocus: false,
    refetchInterval: refreshInterval,
  });

  const secondaryKey = getPaginationKey({
    organizationId,
    collection,
    limit,
    page: pageInt,
    where,
    sort,
    depth,
  });
  // Get data and cache it.
  // Only use this hook if the page is greater than 1.
  // Otherwise, the first page will be fetched by the above
  // and that data will be used.

  const runSecondaryQuery =
    !serialLoading &&
    !userLoading &&
    !countIsLoading &&
    collection &&
    pageInt > 1;

  const { data, isLoading } = useQuery({
    // eslint-disable-next-line @tanstack/query/exhaustive-deps
    queryKey: [secondaryKey],
    queryFn: () =>
      getPaginationData({
        archived,
        collection,
        limit,
        page: pageInt,
        where,
        sort,
        join,
        depth,
        publicView,
      }),
    enabled: runSecondaryQuery,
    refetchOnWindowFocus: false,
    refetchInterval: refreshInterval,
  });

  if (pageInt === 1) {
    return {
      data: countData?.data || [],
      totalPages: countData?.totalPages,
      totalItems: countData?.totalItems,
      currentPage: pageInt,
      isLoading: userLoading || countIsLoading,
    };
  }

  // Otherwise, return data for pages after page 1.
  return {
    data: data?.data || [],
    totalPages: countData?.totalPages,
    totalItems: countData?.totalItems,
    currentPage: pageInt,
    isLoading: userLoading || countIsLoading || isLoading,
  };
}

/**
 * @function usePublicPagination
 * @description Custom hook for managing pagination
 * @param {UsePaginationParams} params - The parameters for the pagination
 * @returns {object} - The pagination data and loading state
 */
export function usePublicPagination(args: UsePaginationParams) {
  return usePaginationHandler({
    ...args,
    userLoading: false,
    organizationId: "public",
    publicView: true,
  });
}

/**
 * @function usePagination
 * @description Custom hook for managing pagination
 * @param {UsePaginationParams} params - The parameters for the pagination
 * @returns {object} - The pagination data and loading state
 */
export default function usePagination<T>(args: UsePaginationParams) {
  const {
    data: { organization },
    isLoading: userLoading,
  } = useCurrentUser();

  const organizationId = organization?.id;

  // TODO - get default page limit from user settings (once it exists in the backend)

  return usePaginationHandler<T>({
    ...args,
    userLoading,
    organizationId,
  });
}
